/**
 * Login/Signup Auth Page
 * Matches portal project's authentication screens:
 * - Login with email/password
 * - Signup with role selection (Buyer/Seller), name, email, password
 * - Google OAuth via Keycloak
 * - OAuth callback handling (inline via URL params)
 * - Email verification messaging
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { sanitizeEmail, sanitizeInput } from '@/lib/sanitize';
import SEO from '@/components/common/SEO';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  User,
  Home,
  Loader2,
  KeyRound,
  X,
  CheckCircle2,
} from 'lucide-react';

const GoogleIcon = () => (
  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup, googleLogin, handleAuthCallback, isAuthenticated } = useAuth();

  // Get returnTo from query params or location state
  const queryParams = new URLSearchParams(location.search);
  const returnTo = queryParams.get('returnTo') || location.state?.from?.pathname || undefined;
  
  const callbackProcessed = useRef(false);

  const [isSignUp, setIsSignUp] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [userRole] = useState('CUSTOMER');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo || '/');
    }
  }, [isAuthenticated, navigate, returnTo]);

  // Handle OAuth callback (code from Keycloak/Google in URL params)
  useEffect(() => {
    const handleCallback = async () => {
      if (callbackProcessed.current) return;
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (code) {
        callbackProcessed.current = true;
        try {
          setLoading(true);
          if (state) {
            localStorage.setItem('pkce_code_verifier', state);
          }
          // Remove code from URL to prevent loop/re-use
          window.history.replaceState({}, document.title, window.location.pathname);

          const success = await handleAuthCallback(code);
          if (success) {
            // Check roles from decoded token to determine redirect
            const token = localStorage.getItem('accessToken');
            if (token) {
              try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const roles = payload?.realm_access?.roles || [];
                if (roles.includes('ADMIN')) {
                  navigate(returnTo || '/admin');
                } else {
                  navigate(returnTo || '/');
                }
              } catch {
                navigate(returnTo || '/');
              }
            } else {
              navigate(returnTo || '/');
            }
          }
        } catch (err) {
          console.error('Auth callback error:', err);
          setError('Failed to authenticate with Google. Please try again.');
          setLoading(false);
        }
      }
    };

    handleCallback();
  }, [location.search, handleAuthCallback, navigate, returnTo]);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const sanitizedEmail = sanitizeEmail(email);
      if (!sanitizedEmail) {
        throw new Error('Please enter a valid email address');
      }

      if (isSignUp) {
        // Signup validation
        if (!firstName.trim()) throw new Error('First name is required');
        if (!lastName.trim()) throw new Error('Last name is required');

        if (password.length < 8) throw new Error('Password must be at least 8 characters');
        if (!/(?=.*[a-z])/.test(password))
          throw new Error('Password must contain at least one lowercase letter');
        if (!/(?=.*[A-Z])/.test(password))
          throw new Error('Password must contain at least one uppercase letter');
        if (!/(?=.*\d)/.test(password))
          throw new Error('Password must contain at least one number');
        if (password !== confirmPassword) throw new Error('Passwords do not match');

        await signup(
          sanitizedEmail,
          password,
          sanitizeInput(userRole),
          returnTo,
          sanitizeInput(firstName.trim()),
          sanitizeInput(lastName.trim())
        );
        setVerificationSent(true);
        setIsSignUp(false);
        setError('');
      } else {
        // Login
        if (password.length === 0) throw new Error('Password is required');
        await login(sanitizedEmail, password, returnTo);
        // AuthContext will trigger redirect via isAuthenticated useEffect
      }
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <SEO title={isSignUp ? "Sign Up" : "Sign In"} noindex />
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-foreground px-8 py-8 text-center">
            <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <span className="text-primary-foreground font-heading font-bold text-2xl">C</span>
            </div>
            <h1 className="text-2xl font-bold text-white">
              {isSignUp ? 'Create an account' : 'Welcome back'}
            </h1>
            <p className="text-white/70 mt-1.5 text-sm">
              {isSignUp
                ? 'Sign up to start buying & selling'
                : 'Sign in to your account to continue'}
            </p>
          </div>

          <div className="px-8 py-8">
            {/* Email Verification Success */}
            {verificationSent && !error && (
              <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
                <p className="font-semibold">Registration successful!</p>
                <p>Please check your email to verify your account before logging in and check spam email also.</p>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl flex gap-3 items-start">
                <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm leading-snug">
                  {error.toLowerCase().includes('email verification') ||
                  error.toLowerCase().includes('verify your email') ? (
                    <span>
                      <strong>📧 Email Verification Required</strong>
                      <br />
                      Please check your email inbox and click the verification link before logging
                      in.
                    </span>
                  ) : (
                    error
                  )}
                </p>
              </div>
            )}

            {/* Google Login Button */}
            <div className="mb-6">
              <button
                type="button"
                onClick={googleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center h-11 rounded-xl border-2 border-gray-200 bg-white text-gray-700 font-medium text-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <GoogleIcon />
                Continue with Google
              </button>
            </div>

            {/* Divider */}
            <div className="relative mb-6 flex items-center gap-4">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">or</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Auth Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Signup-only fields */}
              {isSignUp && (
                <>

                  {/* Name fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                        First Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          id="firstName"
                          type="text"
                          placeholder="First name"
                          className="w-full h-11 pl-11 pr-4 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition placeholder:text-gray-400"
                          value={firstName}
                          onChange={(e) => setFirstName(sanitizeInput(e.target.value))}
                          disabled={loading}
                          autoComplete="given-name"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                        Last Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          id="lastName"
                          type="text"
                          placeholder="Last name"
                          className="w-full h-11 pl-11 pr-4 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition placeholder:text-gray-400"
                          value={lastName}
                          onChange={(e) => setLastName(sanitizeInput(e.target.value))}
                          disabled={loading}
                          autoComplete="family-name"
                          required
                        />
                      </div>
                    </div>
                  </div>


                </>
              )}

              {/* Email */}
              <div className="space-y-1.5">
                <label htmlFor="auth-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    id="auth-email"
                    type="email"
                    placeholder="Enter your email address"
                    className="w-full h-11 pl-11 pr-4 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition placeholder:text-gray-400"
                    value={email}
                    onChange={(e) => setEmail(sanitizeInput(e.target.value))}
                    disabled={loading}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <label htmlFor="auth-password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    id="auth-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    className="w-full h-11 pl-11 pr-12 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition placeholder:text-gray-400"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete={isSignUp ? 'new-password' : 'current-password'}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password (signup only) */}
              {isSignUp && (
                <>
                  <div className="space-y-1.5">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm your password"
                        className="w-full h-11 pl-11 pr-12 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition placeholder:text-gray-400"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading}
                        autoComplete="new-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={loading}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Password requirements */}
                  <div className="rounded-xl bg-primary-soft p-3">
                    <p className="text-sm text-primary font-semibold">Password requirements:</p>
                    <ul className="mt-1 text-sm text-muted-foreground list-disc list-inside space-y-0.5">
                      <li>At least 8 characters</li>
                      <li>One uppercase letter</li>
                      <li>One lowercase letter</li>
                      <li>One number</li>
                    </ul>
                  </div>
                </>
              )}

              {/* Remember me / Forgot password (login only) */}
              {!isSignUp && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary" />
                    Remember me
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {/* Terms (signup only) */}
              {isSignUp && (
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="terms"
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    disabled={loading}
                  />
                  <label htmlFor="terms" className="text-sm text-gray-600">
                    I agree to the{' '}
                    <Link to="/terms" className="text-primary hover:underline font-medium">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-primary hover:underline font-medium">
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-primary text-primary-foreground rounded-xl font-semibold text-sm shadow-md hover:shadow-lg hover:bg-primary/95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSignUp ? 'Create Account' : 'Sign In'}
              </button>
            </form>

            {/* Toggle login/signup */}
            <p className="mt-6 text-center text-sm text-gray-600">
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError('');
                  setVerificationSent(false);
                }}
                disabled={loading}
                className="ml-1 font-semibold text-primary hover:underline disabled:opacity-50"
              >
                {isSignUp ? 'Sign In' : 'Sign Up'}
              </button>
            </p>
          </div>
        </div>

        {/* Footer links */}
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary transition-colors"
          >
            <Home size={16} />
            <span>Back to Home</span>
          </Link>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
        )}
      </div>
    </div>
  );
};

/**
 * ForgotPasswordModal - Collects email and triggers password reset via backend API
 */
const ForgotPasswordModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const { authAPI } = await import('@/lib/api/auth');
      await authAPI.forgotPassword(trimmedEmail);
      setSuccess(true);
    } catch (err) {
      // Still show success for security (don't reveal if email exists)
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-soft">
              <KeyRound className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Reset Password</h2>
              <p className="text-sm text-gray-500">Account recovery</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 transition"
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {success ? (
          <>
            <div className="mb-6 rounded-xl bg-green-50 border border-green-200 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900 mb-1">Check your email</p>
                  <p className="text-sm text-green-700">
                    If an account with that email exists, we've sent a password reset link. Please check your inbox and spam folder.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6 space-y-3 text-sm text-gray-500">
              <p className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                Click the link in the email to set a new password
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                The link expires in 5 minutes
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                After resetting, you'll be redirected to sign in
              </p>
            </div>

            <button
              onClick={onClose}
              className="w-full px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold text-center hover:bg-primary/95 transition"
            >
              Back to Sign In
            </button>
          </>
        ) : (
          <>
            <div className="mb-5 rounded-xl bg-primary-soft border border-primary/20 p-4">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-primary">
                  Enter the email address associated with your account and we'll send you a link to reset your password.
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex gap-2 items-center">
                <AlertCircle size={16} className="text-red-500 shrink-0" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    id="reset-email"
                    type="email"
                    placeholder="Enter your email address"
                    className="w-full h-11 pl-11 pr-4 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition placeholder:text-gray-400"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold text-center hover:bg-primary/95 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send Reset Link
                </button>
              </div>
            </form>
          </>
        )}

        <p className="mt-4 text-center text-xs text-gray-400">
          Can't access your email? Contact{' '}
          <a href="mailto:support@memads.com" className="text-primary hover:underline">
            support@memads.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;

